/**
 * /app/promociones — Gestión de promociones automáticas (Admin UI).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { Plus, BadgePercent } from "lucide-react";
import { CrudEmptyState, CrudHelpCard, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { ClinicalPageShell } from "@/components/clinical";
import { PromotionsTour } from "@/components/ProductTour";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import { toast } from "sonner";
import { PromotionWizard, PromotionListCard } from "@/components/discounts/PromotionWizard";
import type { PromotionFormData } from "@/components/discounts/PromotionWizard";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/promociones")({
  component: PromocionesPage,
  head: () => ({ meta: [{ title: "Promociones — Structura Market" }] }),
});

function PromocionesPage() {
  const tenantId = useWorkspaceTenantId();
  const [showWizard, setShowWizard] = useState(false);
  const { confirm } = useAppDialogs();
  const [editing, setEditing] = useState<PromotionFormData | null>(null);
  const [promos, setPromos] = useState<PromotionFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLock(null);
    try {
      const res = await apiGetJson<{ promotions: PromotionFormData[] }>("/api/promotions", tenantId);
      setPromos(res.promotions ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error("Error al cargar promociones");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form: PromotionFormData) {
    try {
      await apiSendJson(form.id ? `/api/promotions?id=${form.id}` : "/api/promotions", "POST", form, tenantId);
      toast.success(form.id ? "Promoción actualizada" : "Promoción creada");
      await load();
      setShowWizard(false);
      setEditing(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar la promoción");
    }
  }

  async function handleToggle(id: string) {
    try {
      await apiSendJson(`/api/promotions?id=${id}&action=toggle`, "POST", {}, tenantId);
      await load();
    } catch {
      toast.error("Error al cambiar estado");
    }
  }

  async function handleDelete(id: string) {
    if (!(await confirm({ title: "¿Eliminar esta promoción?", destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/promotions?id=${id}`, tenantId);
      toast.success("Promoción eliminada");
      await load();
    } catch {
      toast.error("Error al eliminar");
    }
  }

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <PromotionsTour />
      <MerchantExperienceBar screenId="promociones" />
      <CrudShellHeader
        title="Promociones automáticas"
        description="Configurá 3x2, 2x1 y Nxm — se aplican solos al carrito"
        backHref="/app/descuentos"
        backLabel="Volver a Ventas"
        count={promos.length}
        action={
          !showWizard && !editing ? (
            <Button size="sm" onClick={() => setShowWizard(true)} data-tour="promociones-nueva">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Nueva promoción
            </Button>
          ) : undefined
        }
      />
      <ClinicalPageShell offset="none">
        {loading ? (
          <Loading variant="list" rows={3} />
        ) : (
          <div className="max-w-2xl mx-auto space-y-6 p-4">
            <div className="flex justify-end">
              <RenataLauncher
                entityType="promocion"
                entityId={editing?.id ?? null}
                entityName={editing ? `Promoción` : null}
                field="name"
                intent="improve_text"
                mode="button"
                label="Consultar a Renata sobre esta promoción"
              />
            </div>
            {(showWizard || editing) && (
              <PromotionWizard
                tenantId={tenantId ?? ""}
                initial={editing ?? undefined}
                onSave={handleSave}
                onCancel={() => { setShowWizard(false); setEditing(null); }}
              />
            )}

            {!showWizard && !editing && (
              <div className="space-y-3">
                {promos.length === 0 && (
                  <>
                    <CrudEmptyState
                      icon={<BadgePercent className="h-8 w-8" />}
                      title="No tenés promociones aún"
                      description="Creá una promoción automática y se aplicará sola al carrito cuando se cumplan las condiciones."
                      action={<Button size="sm" onClick={() => setShowWizard(true)}><Plus className="mr-1.5 h-3.5 w-3.5" /> Primera promoción</Button>}
                    />
                    <CrudHelpCard title="¿Cómo funcionan las promociones?">
                      <p>Las promociones se evalúan en tiempo real cuando el cliente agrega productos al carrito.</p>
                      <p className="mt-1">Tipos disponibles: <strong>3×2</strong> (lleva 3 paga 2), <strong>2×1</strong> y <strong>N×M</strong> personalizado.</p>
                    </CrudHelpCard>
                  </>
                )}
                {promos.map((p) => (
                  <PromotionListCard
                    key={p.id}
                    promo={p}
                    onEdit={() => setEditing(p)}
                    onToggle={() => handleToggle(p.id!)}
                    onDelete={() => handleDelete(p.id!)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </ClinicalPageShell>
    </div>
  );
}
